import { useEffect, useRef, useState, type CSSProperties } from 'react'

import './coogo-status-mark.css'

export type CoogoMarkMode = 'brand' | 'activity' | 'diagnostic'

export type CoogoMarkState =
  | 'idle'
  | 'receiving'
  | 'thinking'
  | 'acting'
  | 'verifying'
  | 'waiting_user'
  | 'complete'
  | 'healthy'
  | 'degraded'
  | 'unavailable'

export type CoogoMarkLayer = 'overall' | 'platform' | 'agent' | 'service'
export type CoogoMarkPace = 'fast' | 'normal' | 'deep' | 'held'
export type CoogoMarkSize =
  | 'square'
  | 'inline-standard'
  | 'inline-compact'
  | 'inline-micro'

export interface CoogoStatusMarkProps {
  mode: CoogoMarkMode
  state: CoogoMarkState
  layer?: CoogoMarkLayer
  pace?: CoogoMarkPace
  size: CoogoMarkSize
  label?: string
  className?: string
}

type OperationalLayer = Exclude<CoogoMarkLayer, 'overall'>
type LayerPhase = 'complete' | 'active' | 'pending' | 'affected'

const LAYERS: OperationalLayer[] = ['platform', 'agent', 'service']

const PATHS: Record<OperationalLayer, string> = {
  platform: 'M4,4 L18,20 L4,36',
  agent: 'M24,4 L38,20 L24,36',
  service: 'M44,4 L58,20 L44,36',
}

const COLORS: Record<OperationalLayer, string> = {
  platform: 'hsl(var(--brand-source))',
  agent: '#0EA5E9',
  service: '#8B5CF6',
}

const PACE_SECONDS: Record<CoogoMarkPace, number> = {
  fast: 0.5,
  normal: 1.2,
  deep: 3,
  held: 0,
}

const HELD_STATES = new Set<CoogoMarkState>([
  'waiting_user',
  'complete',
  'healthy',
  'degraded',
  'unavailable',
])

const ACTIVE_STATES = new Set<CoogoMarkState>([
  'receiving',
  'thinking',
  'acting',
  'verifying',
])

const DEFAULT_LABELS: Partial<Record<CoogoMarkState, string>> = {
  receiving: 'Receiving request',
  thinking: 'Preparing response',
  acting: 'Working on the request',
  verifying: 'Checking the result',
  waiting_user: 'Your confirmation is needed',
  complete: 'Ready',
  healthy: 'Operating normally',
  degraded: 'Processing is taking longer than usual',
  unavailable: 'Unable to connect to the service',
}

function currentLayerForState(
  state: CoogoMarkState,
  requestedLayer: CoogoMarkLayer,
): CoogoMarkLayer {
  if (requestedLayer !== 'overall') return requestedLayer
  if (state === 'receiving') return 'platform'
  if (state === 'thinking' || state === 'acting' || state === 'waiting_user') return 'agent'
  if (state === 'verifying' || state === 'unavailable') return 'service'
  return 'overall'
}

function layerPhase(
  layer: OperationalLayer,
  mode: CoogoMarkMode,
  state: CoogoMarkState,
  currentLayer: CoogoMarkLayer,
): LayerPhase {
  if (mode === 'brand' || state === 'idle' || state === 'complete' || state === 'healthy') {
    return 'complete'
  }

  if (currentLayer === 'overall') {
    if (state === 'degraded' || state === 'unavailable') return 'affected'
    return 'active'
  }

  const layerIndex = LAYERS.indexOf(layer)
  const currentIndex = LAYERS.indexOf(currentLayer)
  if (layerIndex < currentIndex) return 'complete'
  if (layerIndex > currentIndex) return 'pending'
  if (state === 'degraded' || state === 'unavailable') return 'affected'
  return 'active'
}

function useMarkVisibility() {
  const ref = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry?.isIntersecting ?? true)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, visible }
}

/**
 * Operational Coogo mark. The SVG uses the exact renewal geometry from the
 * checked-in Studio design documents; state changes only presentation and
 * animation timing, never path coordinates, gaps, or footprint.
 */
export function CoogoStatusMark({
  mode,
  state,
  layer = 'overall',
  pace = 'normal',
  size,
  label,
  className,
}: CoogoStatusMarkProps) {
  const { ref, visible } = useMarkVisibility()
  const resolvedPace: CoogoMarkPace =
    mode === 'brand' || HELD_STATES.has(state) ? 'held' : pace
  const resolvedLayer = currentLayerForState(state, layer)
  const animated =
    mode !== 'brand' &&
    resolvedPace !== 'held' &&
    ACTIVE_STATES.has(state) &&
    visible
  const cycleSeconds = PACE_SECONDS[resolvedPace]
  const resolvedLabel = label ?? DEFAULT_LABELS[state] ?? ''
  const accent = state === 'degraded' ? 'warning' : state === 'unavailable' ? 'danger' : 'none'

  return (
    <span
      ref={ref}
      className={['coogo-status-mark', className].filter(Boolean).join(' ')}
      data-mode={mode}
      data-state={state}
      data-layer={resolvedLayer}
      data-pace={resolvedPace}
      data-size={size}
      data-animated={animated ? 'true' : 'false'}
      data-in-view={visible ? 'true' : 'false'}
      data-testid="coogo-status-mark"
      role={resolvedLabel ? 'status' : undefined}
      aria-live={resolvedLabel ? 'polite' : undefined}
    >
      <span className="coogo-status-mark__visual" data-accent={accent}>
        <svg
          className="coogo-status-mark__svg"
          viewBox="0 0 62 40"
          fill="none"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          focusable="false"
        >
          {LAYERS.map((operationalLayer, index) => {
            const phase = layerPhase(operationalLayer, mode, state, resolvedLayer)
            const delay = cycleSeconds === 0 ? '0s' : `${(cycleSeconds * index) / 3}s`
            const pathStyle = {
              '--coogo-mark-delay': delay,
            } as CSSProperties

            return (
              <path
                key={operationalLayer}
                className="coogo-status-mark__chevron"
                data-chevron={operationalLayer}
                data-phase={phase}
                d={PATHS[operationalLayer]}
                stroke={COLORS[operationalLayer]}
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                pathLength="1"
                style={pathStyle}
              />
            )
          })}
        </svg>
        <span className="coogo-status-mark__accent" aria-hidden="true" />
      </span>

      <span className="coogo-status-mark__copy">
        <span className="coogo-status-mark__label">{resolvedLabel}</span>
        <span className="coogo-status-mark__diagnostic" aria-hidden="true">
          {LAYERS.map((operationalLayer) => (
            <span
              key={operationalLayer}
              className="coogo-status-mark__diagnostic-layer"
              data-diagnostic-layer={operationalLayer}
              data-phase={layerPhase(operationalLayer, mode, state, resolvedLayer)}
            >
              {operationalLayer === 'platform'
                ? 'Platform'
                : operationalLayer === 'agent'
                  ? 'Agent'
                  : 'Service'}
            </span>
          ))}
        </span>
      </span>
    </span>
  )
}
